import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { CreateChartPayload } from '@/entities/chart/api/types';
import {
  CHART_CALCULATION,
  CHART_FIELD,
  CHART_FIELD_ITEM_SELECTION_METHOD,
  CHART_GRAPH,
  CHART_GRAPH_DIRECTION,
  CHART_METRIC,
  CHART_METRIC_VIEW_TYPE,
  CHART_PERIOD_RELATIVE,
  CHART_TERM_TYPE,
  CHART_TERM_UNIT,
  CHART_TYPE,
} from '@/entities/chart/model/constants';
import { ChartDetailPreprocess } from '@/features/(charts)/chart-renderer/lib/ChartPreprocessFactory/ChartPreprocessFactory';
import {
  cleanSeriesOptions,
  getNormalizedHistorical,
  getNormalizedSelectedField,
} from '@/features/(charts)/chart-renderer/lib/ChartPreprocessFactory/helper';
import { COMBO_CHART_ERROR_MESSAGES } from '@/features/(charts)/chart-renderer/model/error-messages';
import useStates from '@/hooks/pure/useStates';
import service from '@/services/index';

type Props = {
  chart_alias?: string;
};

export const initialChartState: ChartState = {
  type: CHART_TYPE.SINGLE_AXIS,
  historical: {
    frequency: null,
    value: false,
  },
  graph_direction: CHART_GRAPH_DIRECTION.VERTICAL,
  graph: CHART_GRAPH.BAR,
  field: {
    type: CHART_FIELD.FEEDBACK_ALL,
    entire: true,
    candidates: [],
  },
  selected_field: {
    method: CHART_FIELD_ITEM_SELECTION_METHOD.TOP_5,
    items: [],
  },
  target: {
    is_compare: false,
    term: {
      type: CHART_TERM_TYPE.PERIODIC,
      relative: CHART_PERIOD_RELATIVE.INCLUDE,
      value: 3,
      unit: CHART_TERM_UNIT.WEEK,
    },
  },
  series: [
    {
      measure: {
        metric: CHART_METRIC.FEEDBACK,
        calculation: CHART_CALCULATION.COUNT,
      },
      options: {
        view_type: CHART_METRIC_VIEW_TYPE.COUNT,
      },
    },
  ],
};

const useChartDetail = (props: Props): ChartContextProps => {
  const mode: EDIT_MODE = props.chart_alias ? 'EDIT' : 'CREATE';

  const [title, setTitle] = useState<string>('');
  const [originalChartInfo, setOriginalChartInfo] = useState<ChartItem | null>(
    null,
  );
  const [initialized, setInitialized] = useState(false);
  const [selectedField, setSelectedField] = useState<ChartSelectedFieldItemDef>(
    initialChartState.selected_field,
  );

  const [tempMetaValues, setTempMetaValues] = useState<
    ChartFieldItemMeta[] | null
  >(null);

  const { state, setState, setValue, initialize } =
    useStates<ChartState>(initialChartState);

  const fetchChartDetail = useCallback(async () => {
    if (!props.chart_alias) {
      setState(initialChartState);
      setInitialized(true);
      return;
    }

    const chartItem = await service.chart.fetchChart(props.chart_alias);
    const normalizedSelectedField =
      getNormalizedSelectedField(chartItem.selected_field, chartItem.graph) ??
      initialChartState.selected_field;

    const nextGraphDirection =
      chartItem.graph_direction ?? initialChartState.graph_direction;
    const nextTarget = chartItem.target ?? initialChartState.target;

    const newState = {
      type: chartItem.type,
      historical: getNormalizedHistorical(
        chartItem.historical ?? initialChartState.historical,
        nextTarget.term,
      ),
      graph: chartItem.graph ?? initialChartState.graph,
      selected_field: normalizedSelectedField,
      target: nextTarget,
      graph_direction: nextGraphDirection,
      field: chartItem.field ?? initialChartState.field,
      series:
        chartItem.series?.map((series) => {
          const cleanedSeries = cleanSeriesOptions(series, chartItem);
          return {
            ...cleanedSeries,
            cross_field: cleanedSeries.cross_field,
          };
        }) ?? initialChartState.series,
    };

    setTitle(chartItem.name);
    setSelectedField(newState.selected_field);
    setOriginalChartInfo({
      ...chartItem,
      selected_field: normalizedSelectedField,
      graph_direction: nextGraphDirection,
    });
    setState(newState);
    setInitialized(true);
  }, [props.chart_alias, setState]);

  useEffect(() => {
    fetchChartDetail();
  }, [fetchChartDetail]);

  const params: CreateChartPayload = useMemo(() => {
    const preprocessFactory = new ChartDetailPreprocess({
      ...state,
      name: title,
    });
    return preprocessFactory.runPreprocess();
  }, [state, title]);

  const paramValidation = useMemo(() => {
    if (state.graph === CHART_GRAPH.COMBO_CHART) {
      if (!state.series[1]?.measure) return false;
    }

    if (state.type === CHART_TYPE.CROSS_AXIS) {
      const crossField = params.series?.[0]?.cross_field;
      if (!crossField) return false;
    }
    return true;
  }, [state, params]);

  const tableQuery = useQuery({
    enabled: initialized && paramValidation,
    queryKey: ['chart-table', params],
    queryFn: () => service.chart.fetchTable(params),
    retry: false,
  });

  useEffect(() => {
    if (tableQuery.data) {
      const field_meta = tableQuery.data.field_item_meta || [];
      setTempMetaValues(field_meta);
    }
  }, [tableQuery.data]);

  const prevTableErrorRef = useRef<unknown>(null);
  useEffect(() => {
    if (tableQuery.error && tableQuery.error !== prevTableErrorRef.current) {
      prevTableErrorRef.current = tableQuery.error;
      console.error('Chart table fetch error:', tableQuery.error);

      if (state.graph === CHART_GRAPH.COMBO_CHART) {
        if (!state.series[1]?.measure) {
          console.warn(
            `COMBO_CHART error: ${COMBO_CHART_ERROR_MESSAGES.SECONDARY_MEASURE_REQUIRED}`,
          );
        }
      }
    }
  }, [tableQuery.error, state.graph, state.series]);

  return {
    mode,
    state,
    setState,
    setValue,
    initialize,
    initialized,
    tableQuery,
    originalChartInfo,
    setOriginalChartInfo,
    title,
    setTitle,
    selectedField,
    setSelectedField,
    tempMetaValues,
    setTempMetaValues,
  };
};

export default useChartDetail;
